import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import EpdSelectComponent from 'components/form/EpdSelectComponent';
import HelpIcon from 'components/icons/HelpIcon';
import { epdSelectStyles, FixedLabel } from 'pages/wizard/styles';
import { WizardStepValidationSchema } from 'pages/wizard/types';
import { Validation } from 'pages/wizard/Validation';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useCpcrsOfMainPcr, usePcrsOfProductCategory, useProductCategories } from 'services/api/standardEpdQueries';
import { ActionState, EPDModel, PCRModel } from 'services/EpdClient';
import { Option } from 'types/types';
import WizardPcrSelectorSectionDownload from './WizardPcrSelectorSectionDownload';
import { NOT_ASSIGNED } from 'constants/constants';
import { isPcrExpired } from 'util/utils';

interface PcrProps {
  epd: EPDModel;
  onChangeEpd: any;
  onChangePcr: (pcr: PCRModel | undefined) => void;
  onChangeCpcr: (cpcr: PCRModel | undefined) => void;
  onRemovePcr: () => void;
  onRemoveCpcr: () => void;
  validation: WizardStepValidationSchema;
}

const WizardPcrSelectorSection: React.FunctionComponent<PcrProps> = ({
  epd,
  onChangePcr,
  onChangeCpcr,
  onRemovePcr,
  onRemoveCpcr,
  onChangeEpd,
  validation,
}) => {
  const { t } = useTranslation();
  const productCategories = useProductCategories().data;
  const pcrs = usePcrsOfProductCategory(epd.productCategoryId).data;
  const cpcrs = useCpcrsOfMainPcr(epd.pcr?.id).data;
  const [nowEpoch] = useState<number>(Date.now());

  const productCategoriesOptions: Option[] | null = useMemo(() => {
    if (!productCategories) {
      return null;
    }
    return productCategories.map((x) => ({ value: x.id!, label: x.title! }));
  }, [productCategories]);

  const pcrOptions: Option[] | null = useMemo(() => {
    if (!pcrs) {
      return null;
    }
    return pcrs.map((x) => ({
      value: x.id!,
      label: x.fullName ?? NOT_ASSIGNED,
      disabled: isPcrExpired(x.validUntil, nowEpoch),
    }));
  }, [pcrs]);

  const cpcrOptions: Option[] | null = useMemo(() => {
    if (!cpcrs) {
      return null;
    }
    return cpcrs.map((x) => ({
      value: x.id!,
      label: x.fullName ?? NOT_ASSIGNED,
      disabled: isPcrExpired(x.validUntil, nowEpoch),
    }));
  }, [cpcrs]);

  const changeProductCategory = (item: SingleValue<Option>) => {
    onChangeEpd('ProductCategory', item?.value);
  };

  if (!productCategoriesOptions) {
    return null;
  }

  const isPcrDisabled = epd.pcrState.actionState === ActionState.Disabled || !pcrOptions;
  const isCpcrDisabled =
    epd.cpcrState.actionState === ActionState.Disabled || !pcrOptions || !cpcrOptions || cpcrOptions.length === 0;

  return (
    <>
      <Row state={epd.productCategoryIdState.actionState}>
        <FixedLabel>
          Product category
          <HelpIcon content="ISO 14025 and EN 15804 emphasize defining product categories clearly in EPDs" />
          <Validation validation={validation} field="productCategoryId" epd={epd} />
        </FixedLabel>
        <EpdSelectComponent
          placeholder={t('placeholder.defaultSelect')}
          onChange={(e: SingleValue<Option>) => changeProductCategory(e)}
          value={productCategoriesOptions.find((o) => o.value === epd.productCategoryId)}
          isMulti={false}
          styles={epdSelectStyles}
          isClearable={true}
          options={productCategoriesOptions}
          isDisabled={epd.productCategoryIdState.actionState === ActionState.Disabled}
        />
      </Row>
      <Row state={isPcrDisabled ? ActionState.Disabled : ActionState.Enabled}>
        <FixedLabel>
          PCR
          <HelpIcon content="Product category rules" />
          <Validation validation={validation} field="pcr" epd={epd} />
        </FixedLabel>
        <EpdSelectComponent
          placeholder={t('placeholder.defaultSelect')}
          value={pcrOptions?.find((o) => o.value === epd.pcr?.id) ?? null}
          onChange={(e: SingleValue<Option>) => {
            if (!pcrs) {
              return;
            }

            if (!e) {
              // Clear button clicked
              onRemovePcr();
              return;
            }

            const newPcr = pcrs.find((pcr) => pcr.id === e.value) ?? ({ id: e.value } as PCRModel);
            onChangePcr(newPcr);
          }}
          options={pcrOptions ?? undefined}
          isDisabled={isPcrDisabled}
          isOptionDisabled={(option: any) => option.disabled}
          styles={epdSelectStyles}
          isClearable={true}
        />
        {epd.pcr && <WizardPcrSelectorSectionDownload pcrType="main" files={epd.pcr.documents || []} />}
      </Row>
      <Row state={isCpcrDisabled ? ActionState.Disabled : ActionState.Enabled}>
        <FixedLabel>
          C-PCR
          <HelpIcon content="Complementary product category rules" />
        </FixedLabel>
        <EpdSelectComponent
          placeholder={t('placeholder.defaultSelect')}
          value={cpcrOptions?.find((o) => o.value === epd.cpcr?.id) ?? null}
          onChange={(e: SingleValue<Option>) => {
            if (!cpcrs) {
              return;
            }

            if (!e) {
              // Clear button clicked
              onRemoveCpcr();
              return;
            }

            const newCpcr = cpcrs.find((pcr) => pcr.id === e.value) ?? ({ id: e.value } as PCRModel);
            onChangeCpcr(newCpcr);
          }}
          options={cpcrOptions ?? undefined}
          isDisabled={isCpcrDisabled}
          isOptionDisabled={(option: any) => option.disabled}
          styles={epdSelectStyles}
          isClearable={true}
        />
        {epd.cpcr && <WizardPcrSelectorSectionDownload pcrType="cpcr" files={epd.cpcr.documents || []} />}
      </Row>
    </>
  );
};

const Row = styled.div<{ state?: ActionState }>`
  display: ${(props) => (props?.state === ActionState.Hidden ? 'none' : 'grid')};
  grid-column-gap: 10px;
  grid-row-gap: 5px;
  justify-content: left;
  align-items: center;
  grid-template-columns: 100%;
  &&& > * {
    min-width: 0; /* ✅ allows shrinking */
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 2fr 4fr 1fr;
  }

  ${(props) =>
    props.state === ActionState.Disabled &&
    css`
      label {
        color: gray;
      }
    `}
`;

export default WizardPcrSelectorSection;
